import { Complete } from "@/components/common/Complete";
import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { useFormsToDo } from "@/features/diaries/features/new/hooks/useFormsToDo";
import {
	randomHappyEmoji,
	wordsOfEncouragement,
	wordsOfPraise,
} from "@/utils/positivePatientWords";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";

const completionMessages = [
	"Great job!",
	"Well done!",
	"Fantastic work!",
	"Excellent progress!",
	"Amazing effort!",
	"Outstanding!",
	"Brilliant work!",
	"Superb job!",
	"Terrific progress!",
	"Wonderful work!",
] as const;

const getRandomMessage = () => {
	const index = Math.floor(Math.random() * completionMessages.length);
	return completionMessages[index] || "Great job!";
};

const EntryComplete = () => {
	const { diaryId } = Route.useParams();
	const [emoji] = useState(() => randomHappyEmoji());
	const [praise] = useState(() => wordsOfPraise());
	const [encouragement] = useState(() => wordsOfEncouragement());
	const [message] = useState(getRandomMessage);

	const { data: diary, isFetching } = useQuery(
		diaryByIdQueryOptions(diaryId as string),
	);

	const { taskCount, isUpToDate, total } = useFormsToDo(
		diary?.trackers_view || [],
		diary?.observations_view || [],
	);

	const remainingForms = total;

	return (
		<Complete
			title={message}
			description={
				isUpToDate
					? `You've completed all your tasks for now. ${praise}`
					: `You have ${taskCount} ${
							taskCount === 1 ? "more tasks" : "more tasks"
						} to complete. ${encouragement}`
			}
			icon={CheckCircle2}
			isLoading={isFetching}
			loadingText="Almost there!"
			loadingDescription="Just a moment..."
			primaryButton={{
				text: !isUpToDate ? "Continue to Next Entry" : "See your Charts",
				to: !isUpToDate
					? "/diaries/$diaryId/home/new"
					: "/diaries/$diaryId/home/charts",
				params: { diaryId },
			}}
		/>
	);
};

export const Route = createFileRoute(
	"/_app/diaries/$diaryId/new/tracker/$trackerId/entry/$entryId/complete",
)({
	component: EntryComplete,
	loader: async ({ params, context }) => {
		await context.queryClient.ensureQueryData(
			diaryByIdQueryOptions(params.diaryId),
		);
		return {};
	},
});
